/**
 * PeerJS connection manager.
 * Wraps Peer + DataConnection lifecycle with typed callbacks.
 */

import Peer from 'peerjs';
import type { DataConnection } from 'peerjs';
import type { NetMessage } from './protocol';

export type PeerState = 'disconnected' | 'connecting' | 'waiting' | 'connected';

export class PeerManager {
  private peer: Peer | null = null;
  private conn: DataConnection | null = null;
  private _state: PeerState = 'disconnected';
  private _isHost = false;
  private _roomCode = '';
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private _latency = 0;

  // Callbacks
  onStateChange: ((state: PeerState) => void) | null = null;
  onConnected: (() => void) | null = null;
  onMessage: ((msg: NetMessage) => void) | null = null;
  onDisconnected: (() => void) | null = null;
  onError: ((err: string) => void) | null = null;

  get state(): PeerState { return this._state; }
  get isHost(): boolean { return this._isHost; }
  get roomCode(): string { return this._roomCode; }
  get latency(): number { return this._latency; }
  get connected(): boolean { return this._state === 'connected'; }

  private setState(s: PeerState): void {
    this._state = s;
    this.onStateChange?.(s);
  }

  /**
   * Host a game room. Creates a Peer with ID 'orbituary-{roomCode}'.
   */
  host(roomCode: string): void {
    this.cleanup();
    this._isHost = true;
    this._roomCode = roomCode;
    this.setState('connecting');

    this.peer = new Peer('orbituary-' + roomCode);

    this.peer.on('open', () => {
      this.setState('waiting');
    });

    this.peer.on('connection', (conn) => {
      this.conn = conn;
      this.setupConnection(conn);
    });

    this.peer.on('error', (err) => {
      this.onError?.(err.type);
      if (this._state !== 'connected') {
        this.setState('disconnected');
      }
    });
  }

  /**
   * Join a game room. Creates anonymous Peer, connects to host.
   */
  join(roomCode: string): void {
    this.cleanup();
    this._isHost = false;
    this._roomCode = roomCode;
    this.setState('connecting');

    this.peer = new Peer();

    this.peer.on('open', () => {
      const conn = this.peer!.connect('orbituary-' + roomCode, { reliable: true });
      this.conn = conn;
      this.setupConnection(conn);
    });

    this.peer.on('error', (err) => {
      this.onError?.(err.type);
      if (this._state !== 'connected') {
        this.setState('disconnected');
      }
    });
  }

  private setupConnection(conn: DataConnection): void {
    conn.on('open', () => {
      this.setState('connected');
      this.onConnected?.();
      this.startPing();
    });

    conn.on('data', (data) => {
      const msg = data as NetMessage;
      if (msg.type === 'pong') {
        this._latency = Math.round((Date.now() - msg.timestamp) / 2);
        return;
      }
      if (msg.type === 'ping') {
        this.send({ type: 'pong', timestamp: msg.timestamp });
        return;
      }
      this.onMessage?.(msg);
    });

    conn.on('close', () => {
      this.setState('disconnected');
      this.stopPing();
      this.onDisconnected?.();
    });

    conn.on('error', () => {
      this.setState('disconnected');
      this.stopPing();
      this.onDisconnected?.();
    });
  }

  send(msg: NetMessage): void {
    if (this.conn && this.conn.open) {
      this.conn.send(msg);
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping', timestamp: Date.now() });
    }, 3000);
  }

  private stopPing(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Graceful disconnect — send goodbye then clean up.
   */
  disconnect(): void {
    if (this.conn && this.conn.open) {
      this.send({ type: 'disconnect' });
    }
    this.cleanup();
  }

  private cleanup(): void {
    this.stopPing();
    if (this.conn) {
      this.conn.close();
      this.conn = null;
    }
    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }
    this._state = 'disconnected';
    this._latency = 0;
  }
}
